/**
 * Share Service
 * Manages project sharing via sharedLinks collection in Firestore
 * Path: sharedLinks/{shareId} (top-level, public read)
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage, app } from "@/config/firebase";
import type { ProjectShare, ShareTimespan, VideoAspectRatio, VideoFPS, VideoJob } from "@/types";
import { generateShareToken } from "@/utils/token";

const functions = getFunctions(app, "europe-west1");

const SHARED_LINKS_COLLECTION = "sharedLinks";

const DEFAULT_TIMESPANS: ShareTimespan[] = ["1d", "1w", "1m"];

/**
 * Read the allowed timespans from a share doc.
 * Prefer `allowedTimespans` (new), fall back to single `timespan` (legacy),
 * or default list if neither is set.
 */
export function getShareTimespans(share: ProjectShare): ShareTimespan[] {
  if (share.allowedTimespans && share.allowedTimespans.length > 0) {
    return share.allowedTimespans;
  }
  if (share.timespan) return [share.timespan];
  return DEFAULT_TIMESPANS;
}

/**
 * Create a new project share
 */
export async function createShare(
  userId: string,
  projectId: string,
  projectName: string,
  photographerName: string,
  config: {
    videoSettings: {
      resolution: "1080p" | "4k";
      fps: VideoFPS;
      aspectRatio: VideoAspectRatio;
    };
    allowedTimespans: ShareTimespan[];
    excludedViewIds?: string[];
    viewOrder?: string[];
    galleryEnabled?: boolean;
    videoEnabled?: boolean;
  }
): Promise<ProjectShare> {
  const shareToken = generateShareToken();

  const shareData = {
    shareToken,
    userId,
    projectId,
    projectName,
    enabled: true,
    photographerName,
    logoUrl: null,
    videoSettings: config.videoSettings,
    allowedTimespans: config.allowedTimespans,
    excludedViewIds: config.excludedViewIds ?? [],
    viewOrder: config.viewOrder ?? [],
    galleryEnabled: config.galleryEnabled ?? true,
    videoEnabled: config.videoEnabled ?? true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, SHARED_LINKS_COLLECTION), shareData);

  return {
    shareId: docRef.id,
    ...shareData,
  } as unknown as ProjectShare;
}

/**
 * Update an existing project share
 */
export async function updateShare(
  shareId: string,
  data: Partial<Pick<ProjectShare,
    | "enabled"
    | "projectName"
    | "photographerName"
    | "logoUrl"
    | "videoSettings"
    | "allowedTimespans"
    | "excludedViewIds"
    | "viewOrder"
    | "galleryEnabled"
    | "videoEnabled"
    | "whatsapp"
  >>
): Promise<void> {
  const docRef = doc(db, SHARED_LINKS_COLLECTION, shareId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get the share for a project (authenticated, by owner)
 */
export async function getShareByProject(
  userId: string,
  projectId: string
): Promise<ProjectShare | null> {
  const q = query(
    collection(db, SHARED_LINKS_COLLECTION),
    where("userId", "==", userId),
    where("projectId", "==", projectId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const docSnap = snapshot.docs[0];
  return {
    shareId: docSnap.id,
    ...docSnap.data(),
  } as ProjectShare;
}

/**
 * Get a share by its public token (unauthenticated)
 */
export async function getShareByToken(
  shareToken: string
): Promise<ProjectShare | null> {
  const q = query(
    collection(db, SHARED_LINKS_COLLECTION),
    where("shareToken", "==", shareToken)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const docSnap = snapshot.docs[0];
  return {
    shareId: docSnap.id,
    ...docSnap.data(),
  } as ProjectShare;
}

/**
 * Delete a project share
 */
export async function deleteShare(shareId: string): Promise<void> {
  const docRef = doc(db, SHARED_LINKS_COLLECTION, shareId);
  await deleteDoc(docRef);
}

/**
 * Subscribe to real-time updates of a share document
 */
export function subscribeToShare(
  shareId: string,
  callback: (share: ProjectShare | null) => void
): () => void {
  const docRef = doc(db, SHARED_LINKS_COLLECTION, shareId);

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({
        shareId: snapshot.id,
        ...snapshot.data(),
      } as ProjectShare);
    } else {
      callback(null);
    }
  });
}

/**
 * Subscribe to real-time updates of a share by public token.
 * Used by the public shared page so photographer edits appear live.
 */
export function subscribeToShareByToken(
  shareToken: string,
  callback: (share: ProjectShare | null) => void
): () => void {
  const q = query(
    collection(db, SHARED_LINKS_COLLECTION),
    where("shareToken", "==", shareToken)
  );

  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }
    const docSnap = snapshot.docs[0];
    callback({
      shareId: docSnap.id,
      ...docSnap.data(),
    } as ProjectShare);
  });
}

/**
 * Upload photographer logo to Firebase Storage
 * Path: logos/{userId}/logo
 */
export async function uploadLogo(
  userId: string,
  file: File
): Promise<string> {
  const logoRef = ref(storage, `logos/${userId}/logo`);
  await uploadBytes(logoRef, file, {
    contentType: file.type,
  });
  return getDownloadURL(logoRef);
}

/**
 * Delete photographer logo from Firebase Storage
 */
export async function deleteLogo(userId: string): Promise<void> {
  const logoRef = ref(storage, `logos/${userId}/logo`);
  try {
    await deleteObject(logoRef);
  } catch (error: unknown) {
    // Ignore "object not found" errors
    if (error instanceof Error && 'code' in error && (error as { code: string }).code === "storage/object-not-found") {
      return;
    }
    throw error;
  }
}

/**
 * Request a shared video generation via callable function.
 * Called by unauthenticated clients from the shared project page.
 * Timespan is passed explicitly for backwards-compat with the old
 * backend that requires it; the new backend uses it only to validate
 * against share.timespan / share.allowedTimespans.
 */
const createSharedVideoJobFn = httpsCallable<
  { shareToken: string; viewId: string; timespan: string },
  { jobId: string; photoCount: number }
>(functions, "createSharedVideoJob");

export async function requestSharedVideo(
  shareToken: string,
  viewId: string,
  timespan: ShareTimespan
): Promise<{ jobId: string; photoCount: number }> {
  const result = await createSharedVideoJobFn({ shareToken, viewId, timespan });
  return result.data;
}

/**
 * Subscribe to a shared video job's progress.
 * Uses the public read rule (shareId != null) for unauthenticated access.
 */
export function subscribeToSharedVideoJob(
  jobId: string,
  callback: (job: VideoJob | null) => void
): () => void {
  const docRef = doc(db, "videoJobs", jobId);

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({
        ...snapshot.data(),
        jobId: snapshot.id,
      } as VideoJob);
    } else {
      callback(null);
    }
  });
}

/**
 * Subscribe to all completed video jobs for a (share, view).
 * Uses the public read rule (shareId != null) for unauthenticated access.
 * Returns most-recent-first.
 */
export function subscribeToSharedVideos(
  shareId: string,
  viewId: string,
  callback: (jobs: VideoJob[]) => void
): () => void {
  const q = query(
    collection(db, "videoJobs"),
    where("shareId", "==", shareId),
    where("viewId", "==", viewId)
  );

  return onSnapshot(q, (snapshot) => {
    const now = Date.now();
    const list: VideoJob[] = [];
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data() as any;
      if (data.status !== "completed" || !data.downloadUrl) continue;
      if (data.expiresAt && data.expiresAt.toMillis?.() < now) continue;
      list.push({ ...data, jobId: docSnap.id } as VideoJob);
    }
    list.sort((a, b) => {
      const av = (a as any).createdAt?.toMillis?.() ?? 0;
      const bv = (b as any).createdAt?.toMillis?.() ?? 0;
      return bv - av;
    });
    callback(list);
  });
}

/**
 * Find the latest fresh cached video job for a (share, view, timespan).
 * A job is considered fresh if:
 *   - status === "completed" and has a downloadUrl
 *   - not expired
 *   - its lastPhotoAt is >= the view's current lastPhotoAt
 *     (no new photos captured since the job was rendered)
 *
 * `viewLastPhotoAt` is the millis timestamp of the view's most recent
 * photo. Pass 0 if unknown — in that case any non-expired job is valid.
 */
export async function findFreshCachedVideo(
  shareId: string,
  viewId: string,
  timespan: ShareTimespan,
  viewLastPhotoAt: number
): Promise<VideoJob | null> {
  const q = query(
    collection(db, "videoJobs"),
    where("shareId", "==", shareId),
    where("viewId", "==", viewId)
  );

  const snapshot = await getDocs(q);
  const now = Date.now();
  let best: VideoJob | null = null;
  let bestCreated = 0;

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    if (data.status !== "completed" || !data.downloadUrl) continue;
    if (data.timespan !== timespan) continue;
    if (data.expiresAt && data.expiresAt.toMillis() < now) continue;

    const jobLastPhoto = data.lastPhotoAt?.toMillis?.() ?? 0;
    if (viewLastPhotoAt > 0 && jobLastPhoto < viewLastPhotoAt) continue;

    const jobCreated = data.createdAt?.toMillis?.() ?? 0;
    if (jobCreated > bestCreated) {
      best = { ...data, jobId: docSnap.id } as VideoJob;
      bestCreated = jobCreated;
    }
  }

  return best;
}
